import { useMemo } from "react";
import { useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PosterCard from "@/components/base/PosterCard";
import EpisodeList from "./components/EpisodeList";
import { movies } from "@/mocks/movies";
import { tvs } from "@/mocks/tvs";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import {
  getGenreRoute,
  getTypeLabel,
  toSummary,
  type MediaSummary,
} from "@/utils/media";
import type { MediaType } from "@/types/media";

interface DetailPageProps {
  type: MediaType;
}

export default function DetailPage({ type }: DetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const isMovie = type === "movie";
  const label = getTypeLabel(type);
  const { isFav, toggle } = useFavorites();

  const movie = isMovie ? movies.find((item) => item.id === id) : undefined;
  const tv = !isMovie ? tvs.find((item) => item.id === id) : undefined;
  const media = movie ?? tv;

  useSeo({
    title: media ? `${media.title}（${media.year}） - 天堂电影网详细介绍` : `${label}详情 - 天堂电影网`,
    description: media
      ? `${media.title}（${media.year}）由${media.director}执导，${media.actors.slice(0, 3).join("、")}主演。${media.description.slice(0, 60)}……天堂电影网提供影片详细介绍与相关推荐。`
      : `天堂电影网${label}详情页。`,
    keywords: media
      ? `${media.title},${media.title}剧情,${media.title}主演,${media.genres.join(",")},天堂电影网`
      : `天堂电影网${label}`,
  });

  const related: MediaSummary[] = useMemo(() => {
    if (!media) return [];
    const pool: MediaSummary[] = isMovie
      ? movies.map((item) => toSummary(item, "movie"))
      : tvs.map((item) => toSummary(item, "tv"));
    return pool
      .filter((item) => item.id !== media.id)
      .map((item) => {
        let score = 0;
        if (item.director === media.director) score += 3;
        score += item.genres.filter((g) => media.genres.includes(g)).length * 2;
        return { item, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((entry) => entry.item);
  }, [media, isMovie]);

  if (!media) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <SiteHeader />
        <main className="flex-1 pt-16 md:pt-20 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
              <i className="ri-file-unknow-line" aria-hidden="true" />
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">未找到该内容</h1>
            <p className="mt-2 text-sm text-foreground-500">你访问的作品可能已被移除或链接有误。</p>
            <a
              href={`/category/${type}`}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回{label}频道
            </a>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorite = isFav(media.id);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <SiteHeader />
      <main className="flex-1 pt-16 md:pt-20">
        <div className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img src={media.poster} alt="" aria-hidden="true" className="w-full h-full object-cover object-top opacity-25 blur-2xl" />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/90 to-background-50" />
          </div>

          <div className="relative w-full px-4 md:px-8 py-8 md:py-12">
            <div className="max-w-[1400px] mx-auto">
              <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑">
                <a href="/" className="hover:text-accent-300 transition-colors cursor-pointer">首页</a>
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
                <a href={`/category/${type}`} className="hover:text-accent-300 transition-colors cursor-pointer">{label}</a>
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
                <span className="text-foreground-800 line-clamp-1">{media.title}</span>
              </nav>

              <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                <div className="w-48 md:w-64 shrink-0 mx-auto md:mx-0">
                  <div className="relative poster-ratio w-full overflow-hidden rounded-lg ring-1 ring-background-200/70">
                    <img
                      src={media.poster}
                      alt={`${media.title}海报`}
                      title={`${media.title} - 天堂电影网`}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">{label}</span>
                    {media.genres.map((genre) => (
                      <a
                        key={genre}
                        href={getGenreRoute(type, genre)}
                        className="rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1 text-xs text-accent-300 hover:bg-accent-500/20 transition-colors cursor-pointer"
                      >
                        {genre}
                      </a>
                    ))}
                  </div>

                  <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
                    {media.title}
                  </h1>

                  <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-400">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-accent-300">
                      <i className="ri-star-fill text-accent-400" aria-hidden="true" />
                      {media.rating.toFixed(1)} 分
                    </span>
                    <span>{media.year}</span>
                    <span>{media.country}</span>
                    {isMovie && movie && <span>{movie.duration} 分钟</span>}
                    {!isMovie && tv && <span>已更新至 {tv.updatedTo} / {tv.episodeCount} 集</span>}
                  </div>

                  <dl className="mt-5 space-y-2 text-sm">
                    <div className="flex gap-2">
                      <dt className="text-foreground-500 w-16 shrink-0">导演</dt>
                      <dd className="text-foreground-800">{media.director}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-foreground-500 w-16 shrink-0">主演</dt>
                      <dd className="text-foreground-800">{media.actors.join(" / ")}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-foreground-500 w-16 shrink-0">更新时间</dt>
                      <dd className="text-foreground-800">{media.updatedAt}</dd>
                    </div>
                  </dl>

                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggle(media.id)}
                      className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        favorite
                          ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                          : "border border-foreground-400/40 text-foreground-800 hover:border-accent-400 hover:text-accent-300"
                      }`}
                    >
                      <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
                      {favorite ? "已收藏" : "收藏"}
                    </button>
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                      <i className="ri-information-line" aria-hidden="true" />
                      收藏仅保存在本地浏览器，无需登录
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-8 py-2 md:py-4">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1.5 h-7 rounded-full bg-primary-500" aria-hidden="true" />
                <h2 className="font-heading text-xl md:text-2xl font-semibold uppercase text-foreground-950">剧情简介</h2>
              </div>
              <p className="text-sm md:text-base text-foreground-400 leading-loose">{media.description}</p>
              <div className="mt-6 rounded-lg bg-secondary-900/60 ring-1 ring-secondary-700/60 p-4 flex gap-3">
                <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-secondary-800 text-accent-300">
                  <i className="ri-play-list-2-line" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">观看与资源说明</h3>
                  <p className="mt-1 text-xs md:text-sm text-foreground-400 leading-relaxed">
                    本站为影视内容导航与信息展示平台，页面所提供的均为影片资料与介绍，不包含任何真实的在线播放接口。如需观看，请前往正规授权的影视平台支持正版。
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-background-100 ring-1 ring-background-200/70 p-5">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4">作品信息</h2>
              <ul className="space-y-3 text-sm">
                <li className="flex justify-between gap-3">
                  <span className="text-foreground-500">类型</span>
                  <span className="text-foreground-800 text-right">{getTypeLabel(type)}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-foreground-500">地区</span>
                  <span className="text-foreground-800 text-right">{media.country}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-foreground-500">年份</span>
                  <span className="text-foreground-800 text-right">{media.year}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-foreground-500">导演</span>
                  <span className="text-foreground-800 text-right">{media.director}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-foreground-500">评分</span>
                  <span className="text-accent-300 text-right font-semibold">{media.rating.toFixed(1)} 分</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {!isMovie && tv && <EpisodeList episodes={tv.episodes} updatedTo={tv.updatedTo} />}

        {related.length > 0 && (
          <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1.5 h-7 rounded-full bg-primary-500" aria-hidden="true" />
                <h2 className="font-heading text-xl md:text-2xl font-semibold uppercase text-foreground-950">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {related.map((item) => (
                  <PosterCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}